import { BarChart3, ChevronDown, Clock, Download, FlaskConical, Globe, Inbox, Printer, Smartphone, Timer, Trophy } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { isFlowKey, languageName } from '@survey/core';
import { api, ApiError, type ResponseFilter } from '../api/client';
import type { AnalysisVariable, ResponseRecord, SurveyDetail, SurveyReport } from '../api/types';
import { AnalysisTools } from '../components/responses/AnalysisTools';
import { CrosstabPanel } from '../components/responses/CrosstabPanel';
import { FilterBar } from '../components/responses/FilterBar';
import { QuestionCard } from '../components/responses/QuestionCard';
import { ResponseDrawer } from '../components/responses/ResponseDrawer';
import { SurveyHeader } from '../components/SurveyHeader';
import { Empty, PageLoader, useToast } from '../components/ui';
import { formatDate, formatDay, formatDuration, formatNumber, percent } from '../format';
import { useI18n } from '../i18n';

type View = 'summary' | 'crosstab' | 'list';
type ExportFormat = 'csv' | 'xlsx' | 'sav';

const PAGE = 25;
// Cross-role response visibility target: updates appear within five seconds while visible.
const RESPONSE_REFRESH_MS = 5_000;

/** Basic response dashboard (FR-22) plus response list and exports (FR-12, FR-23). */
export function ResponsesPage() {
  const { id = '' } = useParams();
  const toast = useToast();
  const { t } = useI18n();
  const [survey, setSurvey] = useState<SurveyDetail | null>(null);
  const [report, setReport] = useState<SurveyReport | null>(null);
  const [rows, setRows] = useState<{ responses: ResponseRecord[]; total: number; unapproved: number } | null>(null);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState('');
  const [open, setOpen] = useState<ResponseRecord | null>(null);
  const [exporting, setExporting] = useState<'' | ExportFormat>('');
  const [filter, setFilter] = useState<ResponseFilter>({});
  const [view, setView] = useState<View>('summary');
  const [variables, setVariables] = useState<AnalysisVariable[]>([]);
  const filterKey = JSON.stringify(filter);

  useEffect(() => {
    api.getSurvey(id).then(setSurvey, (e) => setError(e instanceof ApiError ? e.message : t('common.load_failed')));
  }, [id]);

  const canView = survey?.permissions.includes('responses.view');
  const loadReport = useCallback((silent = false) => {
    api.summary(id, JSON.parse(filterKey) as ResponseFilter).then(setReport, (e) => { if (!silent) setError(e instanceof ApiError ? e.message : t('resp.load_failed')); });
  }, [id, t, filterKey]);

  const loadVariables = useCallback(() => {
    api.variables(id).then((r) => setVariables(r.variables), () => {});
  }, [id]);

  useEffect(() => {
    if (canView) loadVariables();
  }, [canView, loadVariables]);

  const loadResponses = useCallback((silent = false) => {
    api.responses(id, offset, PAGE).then((next) => {
      setRows(next);
      setOpen((current) => current ? next.responses.find((row) => row.id === current.id) ?? current : null);
    }, (e) => { if (!silent) setError(e instanceof ApiError ? e.message : t('resp.list_failed')); });
  }, [id, offset, t]);

  useEffect(() => {
    if (canView) loadReport();
  }, [canView, loadReport]);

  useEffect(() => {
    if (!canView) return;
    loadResponses();
  }, [canView, loadResponses]);

  // Poll only while the tab is visible; response submissions and approvals made by
  // other roles become visible without requiring a manual reload.
  useEffect(() => {
    if (!canView) return;
    const refresh = () => {
      if (document.visibilityState !== 'visible') return;
      loadReport(true);
      loadResponses(true);
    };
    const timer = window.setInterval(refresh, RESPONSE_REFRESH_MS);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [canView, loadReport, loadResponses]);

  async function download(format: ExportFormat) {
    setExporting(format);
    try {
      const name = await api.download(id, format, filter);
      toast(t('resp.downloaded', { name }));
    } catch (e) {
      toast(e instanceof ApiError ? e.message : t('resp.export_failed'), 'error');
    } finally {
      setExporting('');
    }
  }

  async function decide(responseId: string, status: 'approved' | 'rejected') {
    try {
      await api.approveResponse(id,responseId,status);
      const updated=await api.responses(id,offset,PAGE); setRows(updated);
      if(open?.id===responseId) setOpen({...open,approvalStatus:status});
      toast(t(status==='approved'?'approval.response_approved':'approval.response_rejected'));
    } catch(e) { toast(e instanceof ApiError?e.message:t('common.action_failed'),'error'); }
  }

  if (error) return <div className="banner error">{error}</div>;
  if (!survey) return <PageLoader />;
  if (!canView) return <Navigate to={`/surveys/${id}/share`} replace />;
  const canExport = survey.permissions.includes('responses.export');
  const canEdit = survey.permissions.includes('survey.edit');

  return (
    <>
      <SurveyHeader
        id={survey.id}
        number={survey.number}
        code={survey.code}
        status={survey.status}
        permissions={survey.permissions}
        active="responses"
        title={<h1 className="ellipsis" style={{ fontSize: 26, fontWeight: 500 }}>{survey.title || t('common.untitled_questionnaire')}</h1>}
        actions={
          <div className="btn-row no-print">
            <button className="btn" onClick={() => window.print()} title={t('resp.print_hint')}>
              <Printer /> {t('resp.print')}
            </button>
            {canExport && <ExportMenu busy={exporting} disabled={(rows?.unapproved ?? 0) > 0} onPick={(f) => void download(f)} />}
          </div>
        }
      />

      {report && (report.allTotal > 0 || report.testTotal > 0) && (
        <FilterBar def={survey.definition} filter={filter} onChange={setFilter} canWeight={!!report.weighting} testTotal={report.testTotal} />
      )}

      {report && (report.allTotal > 0 || report.testTotal > 0) && (
        <div className="seg mb-16 no-print" role="tablist">
          {(['summary', 'crosstab', 'list'] as View[]).map((v) => (
            <button key={v} role="tab" aria-selected={view === v} className={view === v ? 'active' : ''} onClick={() => setView(v)}>
              {t(`resp.view_${v}`)}
            </button>
          ))}
        </div>
      )}

      {!report ? (
        <PageLoader />
      ) : report.allTotal === 0 && report.testTotal === 0 ? (
        <div className="card">
          <Empty icon={<Inbox />} title={t('resp.none_yet')}>
            <p className="small">
              {survey.status === 'published' ? t('resp.share_to_collect', { link: `/s/${survey.code}` }) : t('resp.publish_to_collect')}
            </p>
          </Empty>
        </div>
      ) : view === 'summary' ? (
        report.total === 0 ? (
          <div className="card">
            <Empty icon={<Inbox />} title={t('resp.no_match')}>
              <p className="small">{t('resp.no_match_hint')}</p>
            </Empty>
          </div>
        ) : (
          <Dashboard report={report} />
        )
      ) : view === 'crosstab' ? (
        <CrosstabPanel surveyId={id} filter={filter} variables={variables} />
      ) : null}

      {(rows?.unapproved ?? 0)>0 && <div className="banner warn">{t('approval.export_blocked',{count:rows!.unapproved})}</div>}

      {view === 'list' && rows && rows.total > 0 && (
        <div className="card mt-16">
          <div className="card-head">
            <div className="tile">
              <BarChart3 />
            </div>
            <div className="grow">
              <h2>{t('resp.all_responses')}</h2>
              <div className="sub">{t('resp.all_sub')}</div>
            </div>
          </div>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('resp.col_submitted')}</th>
                  <th>{t('approval.status')}</th>
                  <th>{t('resp.col_source')}</th>
                  <th>{t('resp.col_respondent')}</th>
                  <th>{t('resp.col_version')}</th>
                  <th className="num">{t('resp.col_answered')}</th>
                  <th className="num">{t('resp.col_time')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.responses.map((r) => (
                  <tr key={r.id} className="clickable" onClick={() => setOpen(r)} tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && setOpen(r)}>
                    <td>{formatDate(r.submittedAt, true)}</td>
                    <td><span className={`pill ${r.approvalStatus==='approved'?'green':r.approvalStatus==='rejected'?'red':'yellow'}`}>{t(`approval.${r.approvalStatus}`)}</span>
                      {survey.permissions.includes('responses.approve') && r.approvalStatus!=='approved' && <button className="btn sm" style={{marginLeft:8}} onClick={(e)=>{e.stopPropagation();void decide(r.id,'approved')}}>{t('approval.approve')}</button>}
                      {survey.permissions.includes('responses.approve') && r.approvalStatus!=='rejected' && <button className="btn sm" style={{marginLeft:8}} onClick={(e)=>{e.stopPropagation();void decide(r.id,'rejected')}}>{t('approval.reject')}</button>}
                    </td>
                    <td>
                      <SourcePill source={r.source} />
                    </td>
                    <td className="muted">{r.respondentEmail ?? t('common.anonymous')}</td>
                    <td>
                      <span className="pill">v{r.surveyVersion}</span>
                    </td>
                    <td className="num">{Object.keys(r.answers).filter((k) => !isFlowKey(k)).length}</td>
                    <td className="num">{formatDuration(r.durationMs)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="pager">
            <span>{t('resp.showing', { from: offset + 1, to: Math.min(offset + PAGE, rows.total), total: formatNumber(rows.total) })}</span>
            <span className="spacer" />
            <button className="btn sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>
              {t('common.previous')}
            </button>
            <button className="btn sm" disabled={offset + PAGE >= rows.total} onClick={() => setOffset(offset + PAGE)}>
              {t('common.next')}
            </button>
          </div>
        </div>
      )}

      {canEdit && report && (
        <AnalysisTools
          surveyId={id}
          published={survey.publishedVersion > 0}
          testTotal={report.testTotal}
          weighting={report.weighting}
          variables={variables}
          onChanged={() => {
            loadReport(true);
            loadResponses(true);
            loadVariables();
          }}
        />
      )}

      {open && (
        <ResponseDrawer
          surveyId={id}
          response={open}
          definition={survey.definition}
          canEdit={canEdit}
          onClose={() => setOpen(null)}
          onSaved={(next) => {
            setOpen(next);
            loadResponses(true);
            loadReport(true);
          }}
        />
      )}
    </>
  );
}

function SourcePill({ source }: { source: 'web' | 'mobile' | 'test' }) {
  const { t } = useI18n();
  if (source === 'test')
    return (
      <span className="pill yellow">
        <FlaskConical size={12} /> {t('resp.test')}
      </span>
    );
  return source === 'mobile' ? (
    <span className="pill blue">
      <Smartphone size={12} /> {t('resp.mobile')}
    </span>
  ) : (
    <span className="pill">
      <Globe size={12} /> {t('resp.web')}
    </span>
  );
}

function Dashboard({ report }: { report: SurveyReport }) {
  const { t } = useI18n();
  const days = useMemo(() => lastDays(report.perDay, 14), [report.perDay]);
  const maxDay = Math.max(1, ...days.map((d) => d.count));
  const peak = days.reduce((a, b) => (b.count > a.count ? b : a), days[0]!);
  const { web, mobile } = report.bySource;
  const test = report.bySource.test ?? 0;

  return (
    <>
      <div className="card kpis mb-16">
        <div className="kpi">
          <div className="tile">
            <Inbox />
          </div>
          <div className="body">
            <div className="label">{t('resp.kpi_total')}</div>
            <div className="value">
              {formatNumber(report.total)}
              {report.weighted && report.weightedTotal != null && <small> · {t('resp.weighted_n', { n: formatNumber(report.weightedTotal) })}</small>}
            </div>
          </div>
        </div>
        <div className="kpi">
          <div className="tile">
            <Smartphone />
          </div>
          <div className="body">
            <div className="label">{t('resp.kpi_channels')}</div>
            <div className="value">
              {formatNumber(web)}
              <small> / {formatNumber(mobile)}</small>
            </div>
          </div>
        </div>
        <div className="kpi">
          <div className="tile">
            <Timer />
          </div>
          <div className="body">
            <div className="label">{t('resp.kpi_avg')}</div>
            <div className="value">{formatDuration(report.avgDurationMs)}</div>
          </div>
        </div>
        <div className="kpi">
          <div className="tile">
            <Clock />
          </div>
          <div className="body">
            <div className="label">{t('resp.kpi_median')}</div>
            <div className="value">{formatDuration(report.medianDurationMs)}</div>
          </div>
        </div>
      </div>

      <div className="grid cols-2 mb-16">
        <div className="card">
          <div className="card-head">
            <div className="grow">
              <h2>{t('resp.per_day')}</h2>
              <div className="sub">{t('resp.per_day_sub', { peak: peak.count > 0 ? `${formatDate(peak.date)} (${peak.count})` : '—' })}</div>
            </div>
          </div>
          <div className="card-pad" style={{ paddingTop: 0 }}>
            <div className="columns-chart" role="img" aria-label={t('resp.per_day_aria')}>
              {days.map((d) => (
                <div key={d.date} className="col" tabIndex={0} data-tip={t('resp.day_tip', { date: formatDate(d.date), count: d.count })} aria-label={t('resp.day_aria', { date: formatDate(d.date), count: d.count })}>
                  <div className={`bar ${d.count ? '' : 'zero'}`} style={{ height: `${(d.count / maxDay) * 100}%` }} />
                </div>
              ))}
            </div>
            <div className="x-axis" aria-hidden>
              {days.map((d, i) => (
                <span key={d.date}>{i % 2 === 0 ? formatDay(d.date) : ''}</span>
              ))}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2 className="grow">{t('resp.channel')}</h2>
          </div>
          <div className="card-pad stack gap-16" style={{ paddingTop: 0 }}>
            <div className="segbar" role="img" aria-label={t('resp.channel_aria', { web, mobile })}>
              {web > 0 && <span style={{ flex: web, background: 'var(--data-1)' }} data-tip={`${t('resp.web')}: ${web} (${percent(web, report.total)})`} tabIndex={0} />}
              {mobile > 0 && <span style={{ flex: mobile, background: 'var(--data-2)' }} data-tip={`${t('resp.mobile')}: ${mobile} (${percent(mobile, report.total)})`} tabIndex={0} />}
              {test > 0 && <span style={{ flex: test, background: 'var(--div-mid)' }} data-tip={`${t('resp.test')}: ${test} (${percent(test, report.total)})`} tabIndex={0} />}
            </div>
            <div className="legend">
              <span>
                <i style={{ background: 'var(--data-1)' }} />
                {t('resp.web')} · {formatNumber(web)} ({percent(web, report.total)})
              </span>
              <span>
                <i style={{ background: 'var(--data-2)' }} />
                {t('resp.mobile')} · {formatNumber(mobile)} ({percent(mobile, report.total)})
              </span>
              {test > 0 && (
                <span>
                  <i style={{ background: 'var(--div-mid)' }} />
                  {t('resp.test')} · {formatNumber(test)} ({percent(test, report.total)})
                </span>
              )}
            </div>
            <p className="small muted">{t('resp.channel_note')}</p>
          </div>
        </div>
      </div>

      {(report.score || report.endings?.length || (report.languages?.length ?? 0) > 1) && (
        <div className="grid cols-3 mb-16">
          {report.score && (
            <div className="card">
              <div className="card-head">
                <div className="tile">
                  <Trophy />
                </div>
                <div className="grow">
                  <h2>{t('resp.score')}</h2>
                  <div className="sub">{t('resp.score_sub', { mean: formatNumber(report.score.mean), min: formatNumber(report.score.min), max: formatNumber(report.score.max) })}</div>
                </div>
              </div>
            </div>
          )}
          {report.endings && report.endings.length > 0 && (
            <div className="card">
              <div className="card-head">
                <h2 className="grow">{t('resp.endings')}</h2>
              </div>
              <div className="card-pad bar-list" style={{ paddingTop: 0 }}>
                {report.endings.map((e) => (
                  <div key={e.id} className="bar-row">
                    <span className="ellipsis">{e.label}</span>
                    <span className="n">
                      {formatNumber(e.count)} · {percent(e.count, report.weightedTotal ?? report.total)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {(report.languages?.length ?? 0) > 1 && (
            <div className="card">
              <div className="card-head">
                <h2 className="grow">{t('runner.language')}</h2>
              </div>
              <div className="card-pad bar-list" style={{ paddingTop: 0 }}>
                {report.languages!.map((l) => (
                  <div key={l.id} className="bar-row">
                    <span className="ellipsis">{languageName(l.id)}</span>
                    <span className="n">
                      {formatNumber(l.count)} · {percent(l.count, report.weightedTotal ?? report.total)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid cols-2">
        {report.questions.map((q, i) => (
          <QuestionCard key={q.id} q={q} n={i + 1} total={report.weightedTotal ?? report.total} />
        ))}
      </div>
    </>
  );
}

function lastDays(perDay: { date: string; count: number }[], n: number) {
  const byDate = new Map(perDay.map((d) => [d.date, d.count]));
  const out: { date: string; count: number }[] = [];
  const today = new Date();
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - i));
    const key = d.toISOString().slice(0, 10);
    out.push({ date: key, count: byDate.get(key) ?? 0 });
  }
  return out;
}

function ExportMenu({ busy, disabled, onPick }: { busy: '' | ExportFormat; disabled: boolean; onPick: (f: ExportFormat) => void }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  return (
    <div className="menu-wrap" ref={ref}>
      <button className="btn" onClick={() => setOpen(!open)} disabled={!!busy || disabled} aria-haspopup="menu" aria-expanded={open} title={disabled ? t('resp.export_blocked') : undefined}>
        <Download /> {busy ? t('resp.exporting') : t('resp.export')} <ChevronDown />
      </button>
      {open && (
        <div className="menu" role="menu">
          {(['csv', 'xlsx', 'sav'] as ExportFormat[]).map((f) => (
            <button
              key={f}
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onPick(f);
              }}
            >
              <strong>{t(`resp.export_${f}`)}</strong>
              <span className="small muted">{t(`resp.export_${f}_hint`)}</span>
            </button>
          ))}
          <p className="small muted menu-note">{t('resp.export_filter_note')}</p>
        </div>
      )}
    </div>
  );
}
